import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Target, X } from 'lucide-react';

export const FocalPointSelector = ({ 
  open, 
  onClose, 
  imageUrl, 
  currentFocalPoint = { x: 50, y: 50 },
  onSave 
}) => {
  const [focalPoint, setFocalPoint] = useState(currentFocalPoint);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (open && imageUrl) {
      // Reset to current focal point when dialog opens
      setFocalPoint(currentFocalPoint);
    }
  }, [open, currentFocalPoint]);

  const handleImageLoad = () => {
    if (imageRef.current && containerRef.current) {
      const img = imageRef.current;
      const container = containerRef.current;
      
      // Get container dimensions
      const containerRect = container.getBoundingClientRect();
      
      // Calculate the displayed image dimensions (respecting object-fit: contain)
      const imageAspect = img.naturalWidth / img.naturalHeight;
      const containerAspect = containerRect.width / containerRect.height;
      
      let displayWidth, displayHeight;
      
      if (imageAspect > containerAspect) {
        // Image is wider - constrained by width
        displayWidth = containerRect.width;
        displayHeight = containerRect.width / imageAspect;
      } else {
        // Image is taller - constrained by height
        displayHeight = containerRect.height;
        displayWidth = containerRect.height * imageAspect;
      }
      
      setImageSize({ width: displayWidth, height: displayHeight });
    }
  };

  const handleClick = (e) => {
    if (!imageRef.current || !containerRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    // Get click position relative to container
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    // Calculate offset due to centering (object-fit: contain centers the image)
    const offsetX = (rect.width - imageSize.width) / 2;
    const offsetY = (rect.height - imageSize.height) / 2;
    
    // Adjust click position to image coordinates
    const imageX = clickX - offsetX;
    const imageY = clickY - offsetY;
    
    // Check if click is within the image bounds
    if (imageX < 0 || imageX > imageSize.width || imageY < 0 || imageY > imageSize.height) {
      return; // Click outside image
    }
    
    // Convert to percentage
    const x = (imageX / imageSize.width) * 100;
    const y = (imageY / imageSize.height) * 100;
    
    setFocalPoint({ x, y });
  };

  const handleSave = () => {
    onSave(focalPoint);
    onClose();
  };

  const handleReset = () => {
    setFocalPoint({ x: 50, y: 50 });
  };

  // Calculate absolute position of the focal point marker
  const getFocalPointPosition = () => {
    if (!imageSize.width || !imageSize.height) return { left: '50%', top: '50%' };
    
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return { left: '50%', top: '50%' };
    
    const offsetX = (containerRect.width - imageSize.width) / 2;
    const offsetY = (containerRect.height - imageSize.height) / 2;
    
    const left = offsetX + (focalPoint.x / 100) * imageSize.width;
    const top = offsetY + (focalPoint.y / 100) * imageSize.height;
    
    return { left: `${left}px`, top: `${top}px` };
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Set Featured Image Focal Point
          </DialogTitle>
          <DialogDescription>
            Click on the image to set the focal point. This determines which part of the image is centered when displayed as a featured photo.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Image Container */}
          <div 
            ref={containerRef}
            className="relative w-full h-[50vh] bg-slate-100 rounded-lg overflow-hidden cursor-crosshair"
            onClick={handleClick}
          >
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Featured"
              className="w-full h-full object-contain"
              onLoad={handleImageLoad}
            />
            
            {/* Focal Point Marker */}
            {imageSize.width > 0 && (
              <div
                className="absolute pointer-events-none"
                style={{
                  ...getFocalPointPosition(),
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10
                }}
              >
                {/* Crosshair */}
                <div className="relative">
                  {/* Center circle */}
                  <div className="w-8 h-8 rounded-full border-4 border-red-500 bg-white/80 shadow-lg" />
                  
                  {/* Horizontal line */}
                  <div className="absolute top-1/2 left-1/2 w-screen h-0.5 bg-red-500/50 -translate-y-1/2" style={{ marginLeft: '-50vw' }} />
                  
                  {/* Vertical line */}
                  <div className="absolute top-1/2 left-1/2 w-0.5 h-screen bg-red-500/50 -translate-x-1/2" style={{ marginTop: '-50vh' }} />
                </div>
              </div>
            )}
          </div>

          {/* Coordinates Display */}
          <div className="flex items-center justify-between text-sm text-slate-600 bg-slate-50 p-3 rounded">
            <span>
              Focal Point: X: {focalPoint.x.toFixed(1)}%, Y: {focalPoint.y.toFixed(1)}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
            >
              Reset to Center
            </Button>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
            <p className="font-medium mb-1">💡 Tip:</p>
            <p>Click anywhere on the image to position the focal point. The red crosshair shows where the image will be centered when displayed.</p>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-[#BF212F] hover:bg-[#8B1923]">
            Save Focal Point
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
